/* eslint-disable semi */
import { SasUtils } from '../libs';

const debug = process.env.NODE_ENV === 'development';

const WILD_CARD = '*';

// 種別
// 0: 完全一致
// 1: 前方一致
// 2: 後方一致
const MATCH = 0;
const FORWARD_MATCH = 1;
const BACKWARD_MATCH = 2;

const matchRules = [
  (name, key) => name === key,
  (name, key) => name.startsWith(key),
  (name, key) => name.endsWith(key)
];

/**
 * 対象メソッドの判定ルールに変換する
 * @param {*} src
 */
function compileCondition (src) {
  let rule = MATCH;
  let key = src;

  if (src.includes(WILD_CARD)) {
    if (src.startsWith(WILD_CARD)) {
      rule = BACKWARD_MATCH;
      key = src.substr(WILD_CARD.length);
    } else {
      rule = FORWARD_MATCH;
      key = src.substr(0, src.length - WILD_CARD.length);
    }
  }

  // 妥当性チェック
  if (key.length === 0 || key.includes(WILD_CARD)) {
    throw Error(`${src} is invalid parameter.`);
  }

  return {
    src: src,
    key: key,
    rule: rule
  };
}

/**
 * 処理対象メソッドか否か判定する
 * @param {*} name メソッド名
 * @param {*} conditons
 */
function isMatchConditionExists (name, conditons) {
  return conditons.some(conditon => {
    return matchRules[conditon.rule](name, conditon.key);
  });
}

/**
 * 判定ルールに対し合致するメソッドが存在するか判定する
 * @param {*} conditon
 * @param {*} methods
 */
function isMatchMethodExists (conditon, methods) {
  return Object.keys(methods).some(name => {
    return matchRules[conditon.rule](name, conditon.key);
  });
}

/**
 * Vueデコレータの呼び出しか判断する
 * falseの場合はデコレータの宣言時の呼び出し
 * @param {*} args
 */
export function isDecoratorCall (args) {
  return args.length > 0 && SasUtils.isVueComponent(args[0]);
}

/**
 * 関数に前処理を注入する
 * @param {*} target
 * @param {*} beforeProcess
 */
export function injectBefore (target, beforeProcess) {
  return wrap(target, beforeProcess, null, false);
}

/**
 * 関数に後処理を注入する
 * @param {*} target
 * @param {*} afterProcess
 * @param {*} async true:後処理を関数が返却したPromiseが完了した時点で実行する
 */
export function injectAfter (target, afterProcess, async = false) {
  return wrap(target, null, afterProcess, async);
}

/**
 * 関数を前処理・後処理を注入する
 * @param {*} target
 * @param {*} beforeProcess
 * @param {*} afterProcess
 * @param {*} async true:後処理を関数が返却したPromiseが完了した時点で実行する
 */
export function wrap (target, beforeProcess, afterProcess, async = false) {
  return function (...args) {
    // ラップ前処理から後処理への連携データ
    let resultOfBefore = null;

    // 前処理の注入
    if (beforeProcess) {
      try {
        resultOfBefore = beforeProcess.apply(this, [args]);
      } catch (e) {
        console.error('beforeProcess', e);
      }
    }

    // 元のメソッドを実行
    let result = target.apply(this, args);

    // 後処理の注入
    if (afterProcess) {
      const afterProcessFunction = () => {
        try {
          afterProcess.apply(this, [args, result, resultOfBefore]);
        } catch (e) {
          console.error('afterProcess', e);
        }
      }
      if (async) {
        result = result
          .then(success => {
            afterProcessFunction();
            return success;
          })
          .catch(reason => {
            afterProcessFunction();
            throw reason;
          });
      } else {
        afterProcessFunction();
      }
    }

    return result;
  };
}

// 複数のメソッドをラップする
export function wrapMethods (methods, targets, before, after, async = false) {
  // コンポーネントにメソッドが宣言されていない場合の補正
  if (methods === null || typeof methods === 'undefined') {
    methods = {};
  }

  // 対象メソッドの抽出条件を生成
  const conditons = targets.map(target => compileCondition(target));

  // 対象メソッドに対する処理の注入を実行
  Object.keys(methods)
    .filter(name => isMatchConditionExists(name, conditons))
    .forEach(name => {
      methods[name] = wrap(methods[name], before, after, async);
    });

  // 実装時の整合性チェック
  // 利用されなかったtargetsがある場合はエラーとして出力
  if (debug) {
    conditons
      .filter(conditon => !isMatchMethodExists(conditon, methods))
      .forEach(conditon => {
        console.error(`${conditon.src} has no match method.`);
      });
  }
}

/**
 * デコレータの生成オプションの整合性チェック
 *
 * NOTE
 * アプリ実装者およびデコレータ実装者の実装時のチェック用処理
 * @param {*} decoratorName
 * @param {*} rules
 * @param {*} options
 */
export function validateDecoratorOptions (decoratorName, rules, options = {}) {
  if (!debug) {
    return;
  }

  // デコレータの生成オプションの整合性チェックの整合性チェック
  // NOTE デコレータ実装者のエラーを検出する
  validateValidateRules(decoratorName, rules);

  // デコレータ側で未定義のオプション指定はエラー
  Object.keys(options)
    .filter(key => !(key in rules))
    .forEach(key => {
      console.error(`${decoratorName}: ${key} is invalid decorator option.`);
    });

  // デコレータの生成オプションのチェック
  // NOTE アプリ実装者のエラーを検出する
  Object.keys(rules).forEach(key => {
    validate(decoratorName, options, key, rules[key]);
  });
}

/**
 * デコレータの生成オプションの整合性チェックの整合性チェック
 * デコレータの生成オプションの整合性チェックでは下記のルール指定のみ可能
 * require boolean 必須
 * type String 型
 * mustBe 必須条件
 * shouldBe 推奨条件
 * mustBeIncludedBy 必須条件（値は指定した配列のいずれかでなければならない）
 * @param {*} decoratorName
 * @param {*} rules
 */
function validateValidateRules (decoratorName, rules) {
  const types = ['string', 'number', 'boolean', 'function', 'array', 'object'];
  const checkes = ['not empty'];

  const ruleRules = {
    require: { type: 'boolean' },
    type: { type: 'string', mustBeIncludedBy: types },
    mustBe: { type: 'array', mustBeIncludedBy: checkes },
    shouldBe: { type: 'array', mustBeIncludedBy: checkes },
    mustBeIncludedBy: { type: 'array' }
  };

  Object.keys(rules).forEach(key => {
    const rule = rules[key];

    // 未定義のルール指定はエラー
    Object.keys(rule)
      .filter(key => !(key in ruleRules))
      .forEach(key => {
        console.error(
          `${decoratorName}(for developer of this decorator): ` +
            `${key} is invalid validate rule.`
        );
      });

    Object.keys(ruleRules).forEach(ruleKey => {
      validate(
        decoratorName + '(for developer of this decorator)',
        rule,
        ruleKey,
        ruleRules[ruleKey]
      );
    });
  });
}

/**
 * バリデーション
 * @param {*} label
 * @param {*} options
 * @param {*} optionName
 * @param {*} rule
 */
function validate (label, options, optionName, rule) {
  const require = rule.require || false;
  const type = rule.type || 'any';
  const mustBe = rule.mustBe || [];
  const shouldBe = rule.shouldBe || [];
  const mustBeIncludedBy = rule.mustBeIncludedBy;

  // オプションの存在チェック
  if (!(optionName in options)) {
    // 指定必須の場合はここでエラー
    if (require) {
      console.error(`${label}: ${optionName} is required option.`);
    }
    // 未指定の場合はチェック終了
    return;
  }

  const value = options[optionName];

  // オプションの型チェック
  if (!isValidType(type, value)) {
    console.error(`${label}: ${optionName} must be ${type}.`);
  }

  // 必須ルールチェック
  mustBe
    .filter(check => !isValidAs(check, value))
    .forEach(check => {
      console.error(`${label}: ${optionName} must be ${check}.`);
    });

  // 推奨ルールチェック
  shouldBe
    .filter(check => !isValidAs(check, value))
    .forEach(check => {
      console.warn(`${label}: ${optionName} should be ${check}.`);
    });

  // 値の制限チェック
  if (mustBeIncludedBy) {
    if (!isIncludedBy(mustBeIncludedBy, value)) {
      console.error(
        `${label}: ${optionName} must be included by ` +
          `${JSON.stringify(mustBeIncludedBy)}.`
      );
    }
  }
}

/**
 * 値がリストに含まれるか判定
 * @param {*} list
 * @param {*} value
 */
function isIncludedBy (list, value) {
  if (Array.isArray(value)) {
    return value.every(val => {
      return list.includes(val);
    });
  } else {
    return list.includes(value);
  }
}

/**
 * 値が指定したルールに合致するか判定
 * @param {*} rule
 * @param {*} value
 */
function isValidAs (rule, value) {
  let valid = false;
  switch (rule) {
    case 'not empty':
      valid = isNotEmpty(value);
      break;
    default:
  }
  return valid;
}

/**
 * 型の判定
 * @param {*} type
 * @param {*} value
 */
function isValidType (type, value) {
  let valid = false;
  switch (type) {
    case 'array':
      valid = Array.isArray(value);
      break;
    case 'string':
      valid = typeof value === 'string';
      break;
    case 'number':
      valid = typeof value === 'number';
      break;
    case 'boolean':
      valid = typeof value === 'boolean';
      break;
    case 'object':
      valid = typeof value === 'object';
      break;
    case 'function':
      valid = typeof value === 'function';
      break;
    case 'any':
      valid = true;
      break;
    default:
  }
  return valid;
}

/**
 * 空値の判定
 * @param {*} value
 */
function isNotEmpty (value) {
  if (value === null) {
    return false;
  }

  const type = typeof value;
  if (type === 'string') {
    return value !== '';
  } else if (type === 'number') {
    return !isNaN(value);
  } else if (type === 'boolean') {
    return true;
  } else if (type === 'function') {
    return true;
  } else if (type === 'object') {
    if (Array.isArray(value)) {
      return value.length > 0;
    } else {
      return Object.keys(value).length > 0;
    }
  } else {
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/core/decorators/utils.js